import React, { useState, useEffect } from 'react';
import { Box, Container, Heading, Text, VStack, SimpleGrid, Button, HStack, Icon, Spinner, Center, useDisclosure, Avatar, Card, CardHeader, CardBody, IconButton, Tag, Link as ChakraLink, Divider } from '@chakra-ui/react';
import { FaCalendarAlt, FaFire, FaTrophy, FaArrowLeft, FaArrowRight, FaBell, FaNewspaper } from 'react-icons/fa';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuthCliente } from '../context/AuthClienteContext';
import { useNotifications } from '../hooks/useNotifications';
import { useAnnouncements } from '../hooks/useAnnouncements'; // ✅ Importamos el nuevo hook
import { collection, query, orderBy, where, limit, updateDoc, arrayUnion, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import PlayerProfileModal from '../components/PlayerProfileModal';
import TournamentBracketModal from '../components/TournamentBracketModal';
import RetaBracketModal from '../components/RetaBracketModal';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale/es';

// ✅ --- TARJETA DE EVENTOS RECIENTES (MODIFICADA PARA CONSISTENCIA VISUAL) ---
function RecentEventCard({ event, onOpenBracket }) {
  const navigate = useNavigate();
  const cardDate = event.createdAt?.toDate().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) || 'N/A';
  const typeTagScheme = event.type === 'tournament' || !event.type ? 'purple' : 'orange';

  // Si es un torneo en progreso
  if (event.status === 'in-progress') {
    return (
      <VStack 
        p={4} 
        bg="white" 
        borderRadius="md" 
        boxShadow="md" 
        justifyContent="space-between" // Asegura que el contenido se distribuya
        h="100%"
        transition="all 0.2s"
       _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
      >
        <VStack>
          {/* Título ahora es un Heading, igual que en los torneos completados */}
          <Heading size="sm" textAlign="center" noOfLines={2}>{event.name}</Heading>
          {/* Se añade el rango de fechas */}
          <Text fontSize="xs" color="gray.500">{event.dates}</Text>
        </VStack>
        <Tag colorScheme="blue" size="sm">En Progreso</Tag>
        <Text textAlign="center" fontSize="xs">Este torneo aún no ha finalizado.</Text>
        <Button size="sm" colorScheme="purple" onClick={() => onOpenBracket(event)}>
          Ver Resultados Actuales
        </Button>
      </VStack>
    );
  }

  // Si está completado
  return (
    <VStack 
      p={4} 
      bg="white" 
      borderRadius="md" 
      boxShadow="md" 
      onClick={() => onOpenBracket(event)} 
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
      h="100%"
      justifyContent="space-between"
    >
      <Heading size="sm" textAlign="center" noOfLines={2}>{event.name}</Heading>
      <Text fontSize="xs" color="gray.500">{cardDate}</Text>
      <Icon as={FaTrophy} w={8} h={8} color="yellow.400" />
      <VStack spacing={0}>
        <Text fontSize="xs">Campeón</Text>
        <Text fontWeight="bold" color="brand.600" textAlign="center">{event.winnerInfo?.name || 'N/A'}</Text>
      </VStack>
      <HStack>
        <Tag size="sm" colorScheme={typeTagScheme}>{event.type === 'reta' ? 'Reta' : 'Torneo'}</Tag>
      </HStack>
    </VStack>
  );
}

function AnnouncementSnippetCard({ anuncio, currentUser, onMarkAsRead }) {
  // Verificamos si el anuncio ha sido leído por el usuario actual
  const isRead = currentUser?.readAnnouncements?.includes(anuncio.id);

  return (
    <ChakraLink as={RouterLink} to={`/portal/comunidad#${anuncio.id}`} onClick={() => onMarkAsRead(anuncio.id)} _hover={{ textDecoration: 'none' }}>
      <Box p={3} bg="gray.50" borderRadius="md" transition="all 0.2s" _hover={{ bg: 'gray.100', transform: 'scale(1.02)'}}>
        <HStack justify="space-between">
          <VStack align="start" spacing={0}>
            <Heading size="sm" noOfLines={1}>{anuncio.title}</Heading>
            <Text fontSize="xs" color="gray.500" noOfLines={2}>{anuncio.content}</Text>
          </VStack>
          <Tag size="sm" colorScheme={isRead ? 'green' : 'red'}>
            {isRead ? 'Leído' : 'No Leído'}
          </Tag>
        </HStack>
      </Box>
    </ChakraLink>
  );
}

export default function HomePage() {
    const { currentUser, setCurrentUser } = useAuthCliente(); 
  const { announcements, loading: loadingAnnouncements } = useAnnouncements({ limit: 3 });
  const { isOpen: isProfileOpen, onOpen: onProfileOpen, onClose: onProfileClose } = useDisclosure();
  const [selectedMember, setSelectedMember] = useState(null);
  const { isOpen: isBracketOpen, onOpen: onBracketOpen, onClose: onBracketClose } = useDisclosure();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [topPlayers, setTopPlayers] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const ITEMS_PER_PAGE = 3;
  const [tournamentsPage, setTournamentsPage] = useState(1);
  const [retasPage, setRetasPage] = useState(1);
  const { notifications } = useNotifications();
  const unreadNotifications = notifications.filter(n => !n.read).slice(0, 3);

  useEffect(() => {
    if (!currentUser?.companyId) return;
    const ref = collection(db, 'companies', currentUser.companyId, 'end_customers');
    const q = query(ref, orderBy('points', 'desc'), limit(5));
    const unsub = onSnapshot(q, snap => setTopPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [currentUser?.companyId]);

  useEffect(() => {
    if (!currentUser?.companyId) return;
    const ref = collection(db, 'companies', currentUser.companyId, 'tournaments');
    const q = query(ref, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setRecentEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [currentUser?.companyId]);

  const handleOpenBracket = (event) => {
    setSelectedEvent(event);
    onBracketOpen();
  };

  const handleClickPlayer = member => {
    setSelectedMember(member);
    onProfileOpen();
  };
  
const handleMarkAsRead = async (announcementId) => {
    if (!currentUser?.uid || !currentUser?.companyId) return;

    // Si ya está leído, no hacemos nada
    if (currentUser.readAnnouncements?.includes(announcementId)) {
      return;
    }

    // 1. Actualización Optimista: Modificamos el estado local INMEDIATAMENTE
    const updatedReadAnnouncements = [...(currentUser.readAnnouncements || []), announcementId];
    setCurrentUser({ ...currentUser, readAnnouncements: updatedReadAnnouncements });

    // 2. Actualización en Segundo Plano: Guardamos en la base de datos
    const userRef = doc(db, 'companies', currentUser.companyId, 'end_customers', currentUser.uid);
    try {
      await updateDoc(userRef, {
        readAnnouncements: arrayUnion(announcementId)
      });
    } catch (error) {
      console.error("Error al marcar el anuncio como leído:", error);
      // Opcional: Si falla, podríamos revertir el estado local
      // y mostrar un toast de error.
    }
  };

  const recentTournaments = recentEvents.filter(e => e.type !== 'reta');
  const recentRetas = recentEvents.filter(e => e.type === 'reta');
  
  const totalTournamentPages = Math.ceil(recentTournaments.length / ITEMS_PER_PAGE);
  const paginatedTournaments = recentTournaments.slice((tournamentsPage - 1) * ITEMS_PER_PAGE, tournamentsPage * ITEMS_PER_PAGE);
  const totalRetaPages = Math.ceil(recentRetas.length / ITEMS_PER_PAGE);
  const paginatedRetas = recentRetas.slice((retasPage - 1) * ITEMS_PER_PAGE, retasPage * ITEMS_PER_PAGE);

  return (
    <Container maxW="container.xl" py={8}>
      <PlayerProfileModal isOpen={isProfileOpen} onClose={onProfileClose} member={selectedMember} />
      {selectedEvent?.type === 'reta' ? 
        <RetaBracketModal isOpen={isBracketOpen} onClose={onBracketClose} tournament={selectedEvent} /> :
        <TournamentBracketModal isOpen={isBracketOpen} onClose={onBracketClose} tournament={selectedEvent} />
      }

      <VStack spacing={10} align="stretch">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
          
          {/* ✅ --- TARJETA DE NOTIFICACIONES CORREGIDA --- */}
          <Box bg="white" p={5} borderRadius="xl" boxShadow="lg">
            <HStack justify="space-between" align="center" mb={4}>
              <Heading size="lg">Mis Notificaciones 🔔</Heading>
              <Button as={RouterLink} to="/portal/notificaciones" size="sm" variant="link" colorScheme="blue">
                Ver todas
              </Button>
            </HStack>
            <VStack spacing={4} align="stretch">
              {unreadNotifications.length > 0 ? (
                unreadNotifications.map(notif => (
                  <ChakraLink as={RouterLink} to="/portal/notificaciones" key={notif.id} _hover={{ textDecoration: 'none' }}>
                    <HStack p={3} bg="blue.50" borderRadius="md" transition="all 0.2s" _hover={{ bg: 'blue.100', transform: 'scale(1.02)' }}>
                      <Icon as={FaBell} color="blue.500" />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="bold">{notif.message}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {notif.createdAt?.toDate() ? formatDistanceToNow(notif.createdAt.toDate(), { addSuffix: true, locale: es }) : ''}
                        </Text>
                      </VStack>
                    </HStack>
                  </ChakraLink>
                ))
              ) : (
                <Text color="gray.500">No hay notificaciones nuevas.</Text>
              )}
            </VStack>
          </Box>
          
          <Box bg="white" p={5} borderRadius="xl" boxShadow="lg">
            <HStack justify="space-between" mb={4}>
                <Heading size="lg">📰 Anuncios del Club</Heading>
                <Button as={RouterLink} to="/portal/comunidad" size="sm" variant="link" colorScheme="blue">Ver todos</Button>
            </HStack>
            <VStack spacing={4} align="stretch">
                {loadingAnnouncements ? <Center><Spinner/></Center> :
                 announcements.length > 0 ? (
                    announcements.map(anuncio => (
                        <AnnouncementSnippetCard 
                            key={anuncio.id} 
                            anuncio={anuncio} 
                            currentUser={currentUser} 
                            onMarkAsRead={handleMarkAsRead}
                        />
                    ))
                 ) : (
                    <Text color="gray.500">No hay anuncios recientes.</Text>
                 )}
            </VStack>
          </Box>
        </SimpleGrid>

        <Box bg="white" p={5} borderRadius="xl" boxShadow="lg">
          <Heading size="lg" mb={4}>Atajos Rápidos</Heading>
          <VStack spacing={3}>
            <Button as={RouterLink} to="/portal/mis-reservas" colorScheme="green" w="full" leftIcon={<Icon as={FaCalendarAlt} />}>Ver Mi Agenda</Button>
            <Button as={RouterLink} to={`/reservas/${currentUser.companyId}`} colorScheme="yellow" w="full" leftIcon={<Icon as={FaCalendarAlt} />} isDisabled={!currentUser.companyId}>Reservar Cancha</Button>
            <Button as={RouterLink} to="/portal/retas-y-torneos" colorScheme="purple" w="full" leftIcon={<Icon as={FaFire} />}>Ver Resultados Retas</Button>
            <Button as={RouterLink} to="/portal/torneos" colorScheme="red" w="full" leftIcon={<Icon as={FaTrophy} />}>Ver Los Torneos</Button>
          </VStack>
        </Box>

        <Box>
          <HStack mb={4} justify="space-between">
            <Heading size="lg"> Top 5 del Club 🏆</Heading>
            <Button as={RouterLink} to="/portal/ranking" size="sm" variant="link" colorScheme="blue">Ver Ranking Completo</Button>
          </HStack>
          {topPlayers.length === 0 ? <Center><Spinner /></Center> :
            <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={6}>
              {topPlayers.map((player, index) => (
                <Card key={player.id} p={4} bg="white" borderRadius="xl" boxShadow="lg" transition="all 0.2s" _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }} onClick={() => handleClickPlayer(player)} cursor="pointer">
                  <CardHeader p={0} mb={2}><Text fontWeight="bold" fontSize="xl" color="gray.400">#{index + 1}</Text></CardHeader>
                  <CardBody p={0} textAlign="center"><Avatar size="lg" name={player.name} src={player.photoURL} mb={2} /><Text fontWeight="bold">{player.name}</Text><Text fontSize="sm" color="brand.600">{player.points} pts</Text></CardBody>
                </Card>
              ))}
            </SimpleGrid>
          }
        </Box>

        <Box>
          <HStack mb={4} justify="space-between">
            <Heading size="lg">Resultados de Torneos</Heading>
            <Button as={RouterLink} to="/portal/torneos" size="sm" variant="link" colorScheme="blue">Ver Historial</Button>
          </HStack>
          {recentTournaments.length === 0 ? <Text color="gray.500">No hay resultados recientes de torneos.</Text> :
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {paginatedTournaments.map(event => <RecentEventCard key={event.id} event={event} onOpenBracket={handleOpenBracket} />)}
              </SimpleGrid>
              {totalTournamentPages > 1 && (
                <HStack justify="center" mt={4}><IconButton icon={<FaArrowLeft />} isDisabled={tournamentsPage === 1} onClick={() => setTournamentsPage(p => p - 1)} aria-label="Anterior" /><Text>Página {tournamentsPage} de {totalTournamentPages}</Text><IconButton icon={<FaArrowRight />} isDisabled={tournamentsPage === totalTournamentPages} onClick={() => setTournamentsPage(p => p + 1)} aria-label="Siguiente" /></HStack>
              )}
            </VStack>
          }
        </Box>
        
        <Box>
          <HStack mb={4} justify="space-between">
            <Heading size="lg">Resultados de Retas</Heading>
            <Button as={RouterLink} to="/portal/retas-y-torneos" size="sm" variant="link" colorScheme="blue">Ver Historial</Button>
          </HStack>
          {recentRetas.length === 0 ? <Text color="gray.500">No hay resultados recientes de retas.</Text> :
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {paginatedRetas.map(event => <RecentEventCard key={event.id} event={event} onOpenBracket={handleOpenBracket} />)}
              </SimpleGrid>
               {totalRetaPages > 1 && (
                <HStack justify="center" mt={4}><IconButton icon={<FaArrowLeft />} isDisabled={retasPage === 1} onClick={() => setRetasPage(p => p - 1)} aria-label="Anterior" /><Text>Página {retasPage} de {totalRetaPages}</Text><IconButton icon={<FaArrowRight />} isDisabled={retasPage === totalRetaPages} onClick={() => setRetasPage(p => p + 1)} aria-label="Siguiente" /></HStack>
              )}
            </VStack>
          }
        </Box>

      </VStack>
    </Container>
  );
}